// Who can see what — the user-first view of the mapping tables.
//
// The React port of the old Windows "Users - Location Mapping" form. Pick a
// user, then move locations, rooms, devices and common devices between
// "available" and "allotted".
//
// ── Why this page exists ───────────────────────────────────────────────────
// A device is only visible when FOUR rows line up (see core/devices/
// deviceAdmin):
//
//   1. DevicesInstalled     the device is installed at a location and room
//   2. UserLocationMapping  the user can open the location
//   3. LocationRoomsMapping the user can see the room inside it
//   4. UserDeviceMapping    the user can see the device inside the room
//
// DeviceAdminScreen writes all four for ONE device. This page is the same data
// from the other end — one user, everything they can reach — which is what you
// need when the complaint is "I can't see my lights" rather than "this device
// is missing".
//
// ── Each level is `everything` minus `allotted` ────────────────────────────
// The left-hand list is never fetched: it is a set difference. The "everything"
// half comes from the endpoints that take no UserId — the same ones the Super
// Admin's All Locations mode uses — so nothing new was needed on the backend.
//
// ⚠️ THE ALLOTTED DEVICE LIST HAS NO ID. GetDeviceDetailsbyUserIdAndRoomId
// returns UniqueNumber and no row id, while every mapping write keys on
// DeviceInstalledID. GetDevicesByRoomID does carry it (`Id`), so ids come from
// the everything-list and are matched by unique number. That is also why the
// two halves cannot be fetched in parallel — the allotted half is annotated
// from the other.
//
// ⚠️ NEVER JUDGE A WRITE BY `affectedRows`. These delete endpoints return counts
// belonging to other tables (see the note in core/api/api.js); the only honest
// confirmation is re-reading the list, which is what every mutation here does.

import {
  getLocations, getAllLocations, getRooms, getAllRoomsAtLocation,
  getDevicesByRoom, getAllDevicesInRoom, getCommonDevices, getDevicesByLocation,
  addUserLocationMapping, deleteUserLocationMapping,
  addLocationRoomsMapping, deleteLocationRoomsMapping,
  addUserDeviceMapping, deleteUserDeviceMapping,
  updateLocationSortOrder, updateRoomSortOrder, updateDeviceSortOrder,
  post,
} from '../api/api';

const rows = (res) => (res && Array.isArray(res.data) ? res.data : []);

export const COMMON_ROOM_ID = 0;

/**
 * Split `all` into what the user already has and what is left to give.
 *
 * Pure, and the only piece of logic in this file worth testing on its own.
 * Matching is on the STRINGIFIED key, because the two endpoints disagree about
 * whether an id is a number or a string more often than they agree.
 */
export function splitAllotted(all, allotted, keyOf) {
  const have = new Set((allotted || []).map((x) => String(keyOf(x))));
  const available = [];
  const assigned = [];
  (all || []).forEach((x) => {
    (have.has(String(keyOf(x))) ? assigned : available).push(x);
  });
  return { available, assigned };
}

/**
 * The allotted rows, in the user's own order, annotated from the full list.
 *
 * Order comes from the ALLOTTED reply because that is where SortOrder lives —
 * the unfiltered endpoints do not return it. Rows the full list does not know
 * about are kept rather than dropped: a device mapped to a user but no longer
 * in the room still needs to be visible here, since un-mapping it is exactly
 * what this page is for.
 */
export function annotateAllotted(allotted, all, keyOf) {
  const byKey = new Map((all || []).map((x) => [String(keyOf(x)), x]));
  return (allotted || []).map((a) => {
    const full = byKey.get(String(keyOf(a)));
    return full ? { ...full, ...a } : a;
  });
}

/**
 * What should stay selected once a list has been re-read.
 *
 * Keeps the current pick IF IT IS STILL THERE, and falls back to the first row
 * otherwise. Both halves matter:
 *
 *   - always taking the first row would throw the operator back to the top of
 *     the page every time they allot something, which on a 110-row list is
 *     worse than no selection at all;
 *   - keeping a pick that has just been unallotted would leave the panels below
 *     showing a room, and devices, belonging to something the user can no
 *     longer reach.
 *
 * -> the row to select, or null when the list is empty.
 */
export function keepOrFirst(current, list, keyOf) {
  const rows_ = list || [];
  if (current != null) {
    const k = String(keyOf(current));
    const still = rows_.find((x) => String(keyOf(x)) === k);
    if (still) return still;
  }
  return rows_[0] || null;
}

// ── Locations ──────────────────────────────────────────────────────────────

export const locationKey = (l) => l?.LocationID;

export async function loadLocations(userId) {
  const [mine, all] = await Promise.all([
    getLocations(userId).then(rows).catch(() => []),
    getAllLocations().then(rows).catch(() => []),
  ]);
  const named = all.map((l) => ({
    ...l,
    // GetAllLocations gives "<owner>-<name>"; the per-user list gives the bare
    // name. Prefer the owner-qualified one — on a list of every location on the
    // server, "Home" appears dozens of times and names nothing.
    DisplayName: l.LocationWithName || l.LocationName || `Location ${l.LocationID}`,
  }));
  const { available } = splitAllotted(named, mine, locationKey);
  const assigned = annotateAllotted(mine, named, locationKey).map((l, i) => ({
    ...l,
    DisplayName: l.DisplayName || l.LocationName || `Location ${l.LocationID}`,
    SortOrder: l.SortOrder ?? i,
  }));
  return { available, assigned };
}

export const allotLocation = (userId, locationId) =>
  addUserLocationMapping(userId, locationId);

export const unallotLocation = (userId, locationId) =>
  deleteUserLocationMapping(userId, locationId);

// ── Rooms ──────────────────────────────────────────────────────────────────

export const roomKey = (r) => r?.RoomID;

export async function loadRooms(userId, locationId) {
  const [mine, all] = await Promise.all([
    getRooms(userId, locationId).then(rows).catch(() => []),
    getAllRoomsAtLocation(locationId).then(rows).catch(() => []),
  ]);
  const { available } = splitAllotted(all, mine, roomKey);
  return { available, assigned: annotateAllotted(mine, all, roomKey) };
}

export const allotRoom = (userId, locationId, roomId) =>
  addLocationRoomsMapping(userId, locationId, roomId);

export const unallotRoom = (userId, locationId, roomId) =>
  deleteLocationRoomsMapping(userId, locationId, roomId);

// ── Devices (in a room, and the location's common ones) ────────────────────

export const deviceKey = (d) => d?.UniqueNumber;

/** The install-row id a mapping write needs, from whichever column carries it. */
export const installIdOf = (d) => d?.Id ?? d?.ID ?? d?.DeviceInstalledID ?? null;

export async function loadRoomDevices(userId, locationId, roomId) {
  const [mine, all] = await Promise.all([
    getDevicesByRoom(userId, roomId, locationId).then(rows).catch(() => []),
    getAllDevicesInRoom(roomId).then(rows).catch(() => []),
  ]);
  const { available } = splitAllotted(all, mine, deviceKey);
  return { available, assigned: annotateAllotted(mine, all, deviceKey) };
}

/**
 * The location's common devices — the ones with no room.
 *
 * There is no unfiltered common-devices endpoint (GetCommonDevicesByLocationID
 * still joins on UserId), so the everything-half is assembled the same way
 * api.js does it for All Locations mode: the location's device list names the
 * roomless ones, and the RoomId 0 rows carry the install id.
 */
export async function loadCommonDevices(userId, locationId) {
  const [mine, atLocation, roomZero] = await Promise.all([
    getCommonDevices(userId, locationId).then(rows).catch(() => []),
    getDevicesByLocation(locationId).then(rows).catch(() => []),
    post('GetDevicesByRoomID', { RoomID: COMMON_ROOM_ID }).then(rows).catch(() => []),
  ]);
  const roomless = new Set(
    atLocation.filter((d) => !d.RoomName).map((d) => String(d.UniqueNumber)),
  );
  const all = roomZero.filter((d) => roomless.has(String(d.UniqueNumber)));
  const { available } = splitAllotted(all, mine, deviceKey);
  return { available, assigned: annotateAllotted(mine, all, deviceKey) };
}

export const allotDevice = (userId, installId) =>
  addUserDeviceMapping(userId, installId);

export const unallotDevice = (userId, installId) =>
  deleteUserDeviceMapping(installId, userId);

// ── Reorder ────────────────────────────────────────────────────────────────
//
// ⚠️ ROOMS ARE THE ODD ONE OUT. Location and device order live on the per-user
// MAPPING tables, so moving them affects only the selected user. A room's
// SortOrder is a column on the Rooms table itself, which is shared — reordering
// rooms here changes the order for EVERY user who can see that room. The old
// Windows form behaves the same way; the panel says so rather than hiding it.

export const reorderLocations = (userId, list) => Promise.all(
  list.map((l, i) => updateLocationSortOrder(userId, l.LocationID, i).catch(() => null)),
);

export const reorderRooms = (list) => Promise.all(
  list.map((r, i) => updateRoomSortOrder(r.RoomID, i).catch(() => null)),
);

export const reorderDevices = (userId, list) => Promise.all(
  list.map((d, i) => {
    const id = installIdOf(d);
    return id == null ? null : updateDeviceSortOrder(userId, id, i).catch(() => null);
  }),
);

/**
 * What the user would still be mapped to if this location were taken away.
 *
 * ⚠️ UNALLOTTING ONE LEVEL DOES NOT CLEAR THE ONES BELOW IT. Drop the location
 * mapping and the room and device rows stay behind — the user cannot see any of
 * it, but the rows are still there, which is the "half-assignment" that makes a
 * device impossible to explain later. The old Windows form leaves them too.
 *
 * So this is not fixed silently in either direction: the count is shown, and
 * removing the rest is the operator's decision.
 *
 * -> { rooms, devices }
 */
export async function danglingUnder(userId, locationId) {
  const [roomsRes, common] = await Promise.all([
    getRooms(userId, locationId).then(rows).catch(() => []),
    getCommonDevices(userId, locationId).then(rows).catch(() => []),
  ]);
  const perRoom = await Promise.all(
    roomsRes.map((r) => getDevicesByRoom(userId, r.RoomID, locationId)
      .then(rows).catch(() => [])),
  );
  const seen = new Set();
  [...perRoom.flat(), ...common].forEach((d) => {
    const u = String(deviceKey(d) ?? '');
    if (u) seen.add(u);
  });
  return { rooms: roomsRes.length, devices: seen.size };
}
